import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { vendorDepositApi } from '../../api/vendorDepositApi';
import { createVendorExpense, getVendorExpenseById, updateVendorExpense } from '../../api/vendorExpenseApi';
import { toast } from 'react-toastify';
import apiClient from '../../api/axios'; // for vendor commission setting

const AddVendorExpenseVendor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [remainingBalance, setRemainingBalance] = useState(0);
  const [vendorCommission, setVendorCommission] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [currencySymbol, setCurrencySymbol] = useState('');

  const [formData, setFormData] = useState({
    deposit_id: '',
    amount: '',
    description: '',
    expense_date: new Date().toISOString().slice(0, 10),
  });

  const urlParams = new URLSearchParams(location.search);
  const depositIdFromUrl = urlParams.get('deposit_id');
  const passedState = location.state || {};
  const expenseFromState = passedState.expense || null;
  const depositIdFromState = passedState.depositId || null;

  // Fetch deposits & vendor commission
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const depositResponse = await vendorDepositApi.getVendorDeposits();
        const vendorDeposits = depositResponse.data || [];
        setDeposits(vendorDeposits);

        // fetch vendor commission default
        const settingsRes = await apiClient.get('/settings');
        const defaultCommission = parseFloat(settingsRes.data?.data['delivery.vendor_commission'] || 0);
        setVendorCommission(defaultCommission);

        let initialForm = { ...formData };
        let initialFinalAmount = 0;

        // 1️⃣ Edit from state
        if (expenseFromState) {
          setIsEditing(true);
          initialForm = {
            deposit_id: expenseFromState.deposit_id?.toString() || '',
            amount: expenseFromState.raw_amount || expenseFromState.amount || '',
            description: expenseFromState.description || '',
            expense_date: expenseFromState.expense_date
              ? new Date(expenseFromState.expense_date).toISOString().slice(0, 10)
              : new Date().toISOString().slice(0, 10),
          };
          setFinalAmount(expenseFromState.amount); // final amount from backend
          const dep = vendorDeposits.find(d => d.deposit_id === expenseFromState.deposit_id);
          if (dep) {
            setRemainingBalance(calculateBalance(dep));
            setCurrencySymbol(dep.currency_symbol || '');
          }
        }

        // 2️⃣ Edit by URL id fallback
        else if (id) {
          setIsEditing(true);
          const res = await getVendorExpenseById(id);
          if (res.success) {
            const expense = res.data;
            initialForm = {
              deposit_id: expense.deposit_id?.toString() || '',
              amount: expense.raw_amount || expense.amount || '',
              description: expense.description || '',
              expense_date: expense.expense_date
                ? new Date(expense.expense_date).toISOString().slice(0, 10)
                : new Date().toISOString().slice(0, 10),
            };
            setFinalAmount(expense.amount); // final amount from backend
            const dep = vendorDeposits.find(d => d.deposit_id === expense.deposit_id);
            if (dep) {
              setRemainingBalance(calculateBalance(dep));
              setCurrencySymbol(dep.currency_symbol || '');
            }
          }
        }

        //  New expense, auto-select deposit
        else if (depositIdFromState || depositIdFromUrl) {
          const depositIdToUse = depositIdFromState || depositIdFromUrl;
          const dep = vendorDeposits.find(d => d.deposit_id.toString() === depositIdToUse);
          if (dep) {
            initialForm.deposit_id = depositIdToUse;
            setRemainingBalance(calculateBalance(dep));
            setCurrencySymbol(dep.currency_symbol || '');
          }
        }

        setFormData(initialForm);
      } catch (error) {
        toast.error('Failed to load deposits or settings');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, depositIdFromUrl, expenseFromState, depositIdFromState]);

  const calculateBalance = (dep) => parseFloat(dep.balance ?? (dep.petty_cash - (dep.expense || 0)));

  // Calculate final amount whenever raw amount changes
  useEffect(() => {
    const amt = parseFloat(formData.amount) || 0;
    const vc = parseFloat(vendorCommission) || 0;

    const final = amt - (amt * vc / 100);
    setFinalAmount(final.toFixed(2));
  }, [formData.amount, vendorCommission]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'deposit_id') {
      const dep = deposits.find(d => d.deposit_id.toString() === value);
      setRemainingBalance(dep ? calculateBalance(dep) : 0);
      setCurrencySymbol(dep?.currency_symbol || '');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(formData.amount);
    if (!formData.deposit_id || isNaN(amount) || amount <= 0) return toast.error('Enter valid amount and deposit');
    if (amount > remainingBalance) return toast.error('Amount exceeds deposit balance');

    const selectedDeposit = deposits.find(d => d.deposit_id.toString() === formData.deposit_id);
    if (!selectedDeposit) return toast.error('Deposit not found');

    setSubmitting(true);
    try {
      const payload = {
        deposit_id: selectedDeposit.deposit_id,
        vendor_id: selectedDeposit.vendor_id,
        raw_amount: amount,
        amount: parseFloat(finalAmount),
        commission_percent: parseFloat(vendorCommission),
        expense_date: formData.expense_date,
        description: formData.description || '',
      };

      if (isEditing) {
      const expenseIdToUpdate = id || expenseFromState?.vendor_expense_id;

      if (!expenseIdToUpdate && isEditing) {
        return toast.error("Expense ID not found for editing");
      }

      await updateVendorExpense(expenseIdToUpdate, payload);
      
        toast.success('Expense updated successfully!');
      } else {
        await createVendorExpense(payload);
        toast.success('Expense added successfully!');
      }

      navigate('/vendordepositcash', { state: { refresh: true } });
    } catch (error) {
      console.error(error?.response?.data || error);
      toast.error(error?.response?.data?.message || `Failed to ${isEditing ? 'update' : 'add'} expense`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header">
          <h5>{isEditing ? 'Edit Expense' : 'Add Expense'}</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">

              {/* Deposit */}
              <div className="col-md-6">
                <label className="form-label">Deposit <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  name="deposit_id"
                  value={formData.deposit_id}
                  onChange={handleChange}
                  required
                  disabled={!!(depositIdFromUrl || isEditing)}
                >
                  <option value="">Select a deposit</option>
                  {deposits.filter(dep => calculateBalance(dep) > 0).map(dep => (
                    <option key={dep.deposit_id} value={dep.deposit_id}>
                      {dep.deposit_id} - {dep.deposit_date} ({dep.currency_symbol || ''} {dep.petty_cash}) | Balance: {dep.currency_symbol || ''} {calculateBalance(dep).toFixed(2)}
                    </option>
                  ))}
                </select>
                {formData.deposit_id && (
                  <small className="text-muted">Remaining Balance: {currencySymbol} {remainingBalance.toFixed(2)}</small>
                )}
              </div>

              {/* Raw Amount */}
              <div className="col-md-6">
                <label className="form-label">Amount <span className="text-danger">*</span></label>
                <div className="input-group">
                  <span className="input-group-text">{currencySymbol}</span>
                  <input
                    type="number"
                    className="form-control"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="Enter amount"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Commission */}
              <div className="col-md-3">
                <label className="form-label">Commission (%)</label>
                <input
                  type="number"
                  className="form-control"
                  value={vendorCommission}
                  readOnly
                />
              </div>

              {/* Final Amount */}
              <div className="col-md-3">
                <label className="form-label">Final Amount</label>
                <input
                  type="text"
                  className="form-control"
                  value={`${currencySymbol} ${finalAmount}`}
                  readOnly
                />
              </div>

              {/* Description */}
              <div className="col-md-6">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-control"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Description (optional)"
                />
              </div>

              {/* Date */}
              <div className="col-md-6">
                <label className="form-label">Date <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  name="expense_date"
                  value={formData.expense_date}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Buttons */}
              <div className="col-12 d-flex gap-2 justify-content-end">
                <button type="button" className="btn btn-light" onClick={() => navigate('/vendordepositcash')} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? (isEditing ? 'Updating...' : 'Saving...') : (isEditing ? 'Update' : 'Save')}
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddVendorExpenseVendor;